// Magic Arbuz Mobile - shared primitives (ESM, wired to the real app).
// Minimal light look (the Dice family on alkanes.app): paper ground, one white widget per
// page, cream blocks inside it, one red action, yellow selection pills. See REBRAND spec.
import { useRef, useEffect } from 'react';
import { useLang } from '../context/LanguageContext';
import { useMobT, MA_T } from './mobI18n';
import { ARBUZ_MINT, MAX_CUSTOM_FEE_RATE } from '../config/constants';
import { I18nLock } from '../components/I18nLock';

export const C = {
  // minimal light palette (Dice family); legacy names kept so every inline style flips:
  // felt/panel = white cards, sunk = cream blocks, gold = ink text, rule = hairlines
  bg:'#fcfbf8', feltSolid:'#ffffff',
  felt:'#ffffff',
  panel:'#ffffff',
  sunk:'#f6f3ee',
  ink:'#343433', dim:'#6b6a68', faint:'#a9a49b',
  gold:'#343433', goldDeep:'#ece7df', goldEdge:'#ece7df', bulb:'#ece7df',
  melon:'#ff3e00', melonDeep:'#e03800', flesh:'#ff3e00', red:'#e5484d',
  rind:'#00a866', rindDeep:'#00a866',
  rule:'#ece7df',
  paper:'#fcfbf8', card:'#ffffff', inner:'#f6f3ee', yellow:'#ffcd6c',
};
export const F = {
  mono:'"JetBrains Mono",monospace',
  body:'Inter,system-ui,sans-serif',
  disp:'"Archivo Black",sans-serif',
  serif:'"Cormorant Garamond",Georgia,serif',
};
export const fmt = n => Math.round(Number(n) || 0).toLocaleString('en-US');
export const VB = ARBUZ_MINT.MIN_TX_SIZE;   // 251
export const SVC = ARBUZ_MINT.SERVICE_FEE;  // 1069
export const CARD_VB = 300;

export const FELT_BG = '#fcfbf8';

// ── helper tokens (shape / type) ─────────────────────────────────────────────
export const SH = {
  pill: '0 1px 4px rgba(38,32,26,.12)',            // white pill on cream
  widget: '0 1px 2px rgba(38,32,26,.04)',          // the one white widget
  line: `inset 0 0 0 1.5px ${C.rule}`,             // input / track border
  ink: `inset 0 0 0 1.5px ${C.ink}`,
};
export const CTA_OFF = '#f0c6b6';                  // disabled primary button
export const SCRIM = 'rgba(38,32,26,.32)';         // sheet backdrop (max opacity)
// tiny uppercase label (11px, .6 tracking, faint)
export const LBL = { fontFamily:F.body, fontSize:11, fontWeight:700, letterSpacing:.6, textTransform:'uppercase', color:C.faint };
// plain heading face (sentence case)
export const HEAD = (size = 15) => ({ fontFamily:F.body, fontWeight:800, fontSize:size, letterSpacing:size >= 24 ? -1 : -.2, lineHeight:1.1, color:C.ink });
export const NUM = (size = 17) => ({ fontFamily:F.mono, fontWeight:700, fontSize:size, fontVariantNumeric:'tabular-nums', color:C.ink });

// prefers-reduced-motion: decorative motion is gone already; this also stills the
// functional ones that can safely rest (count-ups jump, pulses hold, sheets snap).
export const reduceMotion = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
export const anim = (a) => (reduceMotion ? 'none' : a);
export const trans = (t) => (reduceMotion ? 'none' : t);

// ── Animated count-up number (ref + rAF, no setState - stays off React's render path) ──
export function CountUp({ value, dur = 750, from0 = false, format, decimals = 0 }) {
  const ref = useRef(null);
  const prev = useRef(from0 && !reduceMotion ? 0 : (Number(value) || 0));
  const fn = format || (n => decimals ? Number(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : fmt(n));
  useEffect(() => {
    const el = ref.current; const from = prev.current, to = Number(value) || 0; prev.current = to;
    if (!el) return;
    if (from === to || reduceMotion) { el.textContent = fn(to); return; }
    let raf, start;
    const tick = ts => { if (!start) start = ts; const t = Math.min(1, (ts - start) / dur); const e = 1 - Math.pow(1 - t, 3);
      el.textContent = fn(from + (to - from) * e); if (t < 1) raf = requestAnimationFrame(tick); else el.textContent = fn(to); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, dur]);
  return <span ref={ref}>{fn(prev.current)}</span>;
}

// ── loading skeleton ─────────────────────────────────────────────────────────
export function Skel({ w = '100%', h = 14, r = 8, style }) { return <div className="skel" style={{ width: w, height: h, borderRadius: r, ...style }} />; }

// ── spinner (functional) ─────────────────────────────────────────────────────
export function Spin({ size = 18, color = C.ink, track = C.rule, width = 2 }) {
  return <span aria-hidden="true" style={{ width:size, height:size, borderRadius:'50%', display:'inline-block', flexShrink:0, boxSizing:'border-box',
    border:`${width}px solid ${track}`, borderTopColor:color, animation:'mob-spin .7s linear infinite' }} />;
}

// ── layout blocks ────────────────────────────────────────────────────────────
// the one white widget per page
export function Widget({ children, pad = 14, gap = 10, style }) {
  return <div style={{ background:C.card, borderRadius:28, padding:pad, display:'flex', flexDirection:'column', gap, boxShadow:SH.widget, minWidth:0, ...style }}>{children}</div>;
}
// cream block inside the widget
export function Block({ children, pad = 18, style }) {
  return <div style={{ background:C.inner, borderRadius:20, padding:pad, minWidth:0, ...style }}>{children}</div>;
}
// block header: small gray text left, bold ink right
export function BlockHead({ left, right, style }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:10, fontFamily:F.body, fontSize:13, fontWeight:600, color:C.dim, marginBottom:10, ...style }}>
      <span style={{ minWidth:0 }}>{left}</span>{right != null && <b style={{ color:C.ink, fontWeight:700, flexShrink:0 }}>{right}</b>}
    </div>
  );
}
// stat (label tiny faint uppercase, value mono ink)
export function Stat({ label, value, align = 'left', size = 15, color }) {
  return (
    <div style={{ display:'grid', gap:2, textAlign:align, minWidth:0 }}>
      <span style={{ ...LBL, whiteSpace:'nowrap' }}>{label}</span>
      <b style={{ ...NUM(size), color:color || C.ink, whiteSpace:'nowrap' }}>{value}</b>
    </div>
  );
}

// ── fanned hand of possible card symbols (light) ─────────────────────────────
const FAN_EMOJIS = ['🍉','⭐','🐉','🥀','🎩','🦁','🪬'];
export function FannedDeck({ height = 180 }) {
  return (
    <div aria-hidden="true" style={{position:'relative',height,display:'flex',alignItems:'flex-end',justifyContent:'center',animation:anim('mob-fanfloat 4s ease-in-out infinite')}}>
      {FAN_EMOJIS.map((e,i,a)=>{
        const rot=(i-(a.length-1)/2)*9;
        return (
          <div key={i} style={{position:'absolute',bottom:16,left:'50%',marginLeft:-46,width:92,height:144,borderRadius:16,
            transformOrigin:'50% 142%',transform:`rotate(${rot}deg)`,zIndex:i,
            background:C.card,boxShadow:`${SH.line},${SH.pill}`,
            display:'flex',alignItems:'center',justifyContent:'center'}}>
            <span style={{fontSize:34,lineHeight:1}}>{e}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── $ARBUZ glyph ─────────────────────────────────────────────────────────────
export function ChipIcon({ size = 28 }) {
  return (
    <span role="img" aria-label="ARBUZ" style={{ fontSize: Math.round(size * .86), lineHeight: 1, display: 'inline-block', flexShrink: 0 }}>🍉</span>
  );
}

// ── brand: small red rounded square + "Magic Arbuz" ──────────────────────────
export function BrandMark({ size = 22, text = true, fontSize = 15 }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:8, minWidth:0 }}>
      <i style={{ width:size, height:size, borderRadius:Math.round(size * .32), background:C.melon, display:'grid', placeItems:'center', flexShrink:0 }}>
        <span style={{ width:Math.round(size * .36), height:Math.round(size * .36), borderRadius:'50%', background:'#fff' }} />
      </i>
      {text && <span style={{ fontFamily:F.body, fontWeight:800, fontSize, letterSpacing:-.2, color:C.ink, whiteSpace:'nowrap' }}>Magic Arbuz</span>}
    </span>
  );
}

// ── Language switch - one ghost pill (shows the language you'll switch TO) ────
export function LangSeg({ lang, setLang }) {
  return (
    <button type="button" onClick={()=>setLang(lang==='en'?'zh':'en')} className="tap"
      aria-label={lang==='en'?'Switch to 中文':'Switch to English'}
      style={{height:32,padding:'0 12px',flexShrink:0,whiteSpace:'nowrap',cursor:'pointer',userSelect:'none',
        display:'inline-flex',alignItems:'center',justifyContent:'center',
        border:'none',borderRadius:999,background:'transparent',boxShadow:SH.line,
        fontFamily:F.body,fontWeight:700,fontSize:12.5,color:C.ink}}>
      <I18nLock ghosts={['中文','EN']}>{lang==='en'?'中文':'EN'}</I18nLock>
    </button>
  );
}
// header lang toggle - wired to the app's global LanguageContext
export function LangToggle() {
  const { lang, setLang } = useLang();
  return <LangSeg lang={lang} setLang={setLang} />;
}

// wallet as a white pill (tap = disconnect)
export function WalletPill({ addr, onClick }) {
  if (!addr) return null;
  return (
    <button type="button" onClick={onClick} style={{display:'inline-flex',alignItems:'center',gap:6,height:32,padding:'0 12px',flexShrink:0,
      border:'none',borderRadius:999,background:C.card,boxShadow:SH.pill,cursor:'pointer',whiteSpace:'nowrap'}}>
      <span style={{width:6,height:6,borderRadius:'50%',background:C.rind}}/>
      <span style={{fontFamily:F.mono,fontSize:11.5,fontWeight:500,color:C.ink}}>{addr}</span>
    </button>
  );
}

// top bar: brand (or a leading control) left, language + wallet right. Sits on paper.
export function MobTopBar({ addr, onDisc, leading, sticky = true, lang = true }) {
  return (
    <div style={{position:sticky?'sticky':'relative',top:0,zIndex:40,flexShrink:0,background:C.paper,
      display:'flex',alignItems:'center',gap:8,padding:'12px 16px 10px',minHeight:54,boxSizing:'border-box'}}>
      <div style={{minWidth:0,flex:1,display:'flex',alignItems:'center'}}>{leading || <BrandMark/>}</div>
      {lang && <LangToggle/>}
      <WalletPill addr={addr} onClick={onDisc}/>
    </div>
  );
}

// page title (30px / 800) with an optional right-hand slot (stats)
export function PageTitle({ children, right, sub }) {
  return (
    <div style={{padding:'6px 16px 14px',display:'flex',justifyContent:'space-between',alignItems:'flex-end',gap:'8px 16px',flexWrap:'wrap'}}>
      <div style={{minWidth:0}}>
        <h1 style={{margin:0,...HEAD(30),letterSpacing:-1.2,lineHeight:1}}>{children}</h1>
        {sub && <div style={{marginTop:6,fontFamily:F.body,fontSize:13,color:C.dim}}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

// ── page header = top bar + plain title ──────────────────────────────────────
export function MobHeader({ title, sub, addr, onDisc, leading, right }) {
  return (
    <>
      <MobTopBar addr={addr} onDisc={onDisc} leading={leading}/>
      {title && <PageTitle sub={sub} right={right}>{title}</PageTitle>}
    </>
  );
}

// ── Shared primitives ────────────────────────────────────────────────────────
export function Ey({ children, color }) { return <div style={{...LBL,color:color||C.faint}}>{children}</div>; }
export function Mon({ children, size=14, color, dim, style }) { return <span style={{fontFamily:F.mono,fontSize:size,color:color||(dim?C.dim:C.ink),fontVariantNumeric:'tabular-nums',...style}}>{children}</span>; }
export function Bod({ children, size=14, dim, style }) { return <span style={{fontFamily:F.body,fontSize:size,fontWeight:500,color:dim?C.dim:C.ink,lineHeight:1.55,...style}}>{children}</span>; }
export function Di({ children, size=20, color }) { return <span style={{fontFamily:F.body,fontSize:size,color:color||C.ink,fontWeight:800,letterSpacing:-.2}}>{children}</span>; }
export function Dot({ color=C.melon, size=6, pulse }) { return <span style={{width:size,height:size,borderRadius:'50%',background:color,display:'inline-block',flexShrink:0,animation:pulse?anim('mob-dotpulse 1.3s ease-in-out infinite'):'none'}}/>; }

// small status tag: neutral = white/gray, ok = green bg white, err = red text
export function Tag({ children, kind = 'neutral', style }) {
  const k = kind === 'ok' ? { background:C.rind, color:'#fff' }
    : kind === 'err' ? { background:C.card, color:C.red, boxShadow:`inset 0 0 0 1.5px ${C.rule}` }
    : kind === 'line' ? { background:'transparent', color:C.dim, boxShadow:SH.line }
    : { background:C.card, color:C.dim };
  return <span style={{display:'inline-flex',alignItems:'center',gap:5,fontFamily:F.body,fontWeight:700,fontSize:11,padding:'3px 9px',borderRadius:999,whiteSpace:'nowrap',...k,...style}}>{children}</span>;
}

// selection pill (yellow when on) - presets, filters, fee rates, tabs
export function Pill({ on, onClick, children, disabled, style, title, ...rest }) {
  return (
    <button type="button" onClick={disabled?undefined:onClick} disabled={disabled} title={title} {...rest} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,
      padding:'7px 12px',border:'none',borderRadius:999,cursor:disabled?'default':'pointer',whiteSpace:'nowrap',flexShrink:0,
      background:on?C.yellow:C.card,boxShadow:on?'none':SH.line,color:C.ink,opacity:disabled?.45:1,
      transition:trans('background .2s, box-shadow .2s'),animation:on?anim('mob-tick .3s cubic-bezier(.34,1.5,.55,1)'):'none',
      fontFamily:F.body,fontWeight:on?700:600,fontSize:12.5,...style}}>{children}</button>
  );
}

// segmented control: white track with inset line, selected = yellow
export function Seg({ children, style, role = 'tablist' }) {
  return <div role={role} style={{display:'flex',gap:4,padding:4,borderRadius:999,background:C.card,boxShadow:SH.line,...style}}>{children}</div>;
}
// tabs (Minting / Claiming, rules sections): selected = ink with white text, as on desktop; yellow is for value pills
export function SegItem({ on, onClick, children, disabled, style, role = 'tab', ...rest }) {
  return (
    <div role={role} aria-selected={role==='tab'?!!on:undefined} onClick={disabled?undefined:onClick} {...rest}
      style={{flex:1,minWidth:0,display:'flex',alignItems:'center',justifyContent:'center',gap:7,padding:'9px 6px',borderRadius:999,
        cursor:disabled?'not-allowed':'pointer',userSelect:'none',WebkitTapHighlightColor:'transparent',
        background:on?C.ink:'transparent',color:on?'#ffffff':C.dim,fontFamily:F.body,fontWeight:700,fontSize:12.5,whiteSpace:'nowrap',
        transition:trans('background .25s ease, color .25s ease'),
        opacity:disabled?.45:1,...style}}>{children}</div>
  );
}

// text link button (underlined 800)
export function LinkBtn({ children, onClick, disabled, style, ...rest }) {
  return (
    <button type="button" onClick={disabled?undefined:onClick} disabled={disabled} {...rest} style={{border:'none',background:'none',padding:'4px 2px',cursor:disabled?'default':'pointer',
      fontFamily:F.body,fontWeight:800,fontSize:12.5,color:disabled?C.faint:C.ink,textDecoration:'underline',textUnderlineOffset:2,whiteSpace:'nowrap',opacity:disabled?.6:1,...style}}>{children}</button>
  );
}

// ── Routing toggle ───────────────────────────────────────────────────────────
export function RoutingToggle({ route, setRoute }) {
  const { t:T } = useMobT();
  const opts=[{k:'public',t:T('rt_public'),s:T('rt_publicSub')},{k:'rebar',t:T('rt_rebar'),s:T('rt_rebarSub'),disabled:true}];
  return (
    <div style={{display:'flex',gap:4,padding:4,borderRadius:18,background:C.card,boxShadow:SH.line,marginTop:8}}>
      {opts.map(m=>{
        const on=route===m.k;
        return (
          <div key={m.k} onClick={()=>{ if(m.disabled) return; setRoute(m.k); }} style={{flex:1,minWidth:0,padding:'9px 12px',borderRadius:14,cursor:m.disabled?'not-allowed':'pointer',
            background:on?C.yellow:'transparent',opacity:m.disabled?.45:1,transition:trans('background .25s ease')}}>
            <div style={{fontFamily:F.body,fontWeight:800,fontSize:14,color:on?C.ink:C.dim}}>{m.t}</div>
            <div style={{fontFamily:F.body,fontWeight:500,fontSize:11,color:on?C.dim:C.faint,marginTop:1}}>{m.s}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Fee rate pills (live values) - Dice-style text pills ─────────────────────
const FEE_KEYS=['slow','normal','fast','custom'];
export function FeeChips({ fee, setFee, cv, setCv, vals, disabled }) {
  const { t } = useMobT();
  return (
    <div style={{display:'flex',flexWrap:'wrap',justifyContent:'center',gap:5,marginTop:10,opacity:disabled?.45:1,pointerEvents:disabled?'none':'auto'}}>
      {FEE_KEYS.map(k=>{
        const on=fee===k;
        const shown = vals?.[k] ?? '-';
        return (
          <div key={k} onClick={()=>setFee(k)} style={{display:'inline-flex',alignItems:'center',gap:5,height:34,padding:'0 11px',borderRadius:999,cursor:'pointer',flexShrink:0,
            background:on?C.yellow:C.card,boxShadow:on?'none':SH.line,color:C.ink,fontFamily:F.body,fontWeight:on?700:600,fontSize:12,whiteSpace:'nowrap',
            transition:trans('background .2s, box-shadow .2s'),animation:on?anim('mob-tick .3s cubic-bezier(.34,1.5,.55,1)'):'none'}}>
            <I18nLock en={MA_T.en['fee_'+k]}>{t('fee_'+k)}</I18nLock>
            {k==='custom'
              ? (on
                ? // type="text": with type="number" WebKit reports an empty value while a
                  // locale comma is being typed, so the comma→dot rewrite never runs.
                  <input type="text" inputMode="decimal" autoFocus value={cv} aria-label="sat/vB"
                    onChange={e=>{const v=e.target.value.replace(/,/g,'.').replace(/[^0-9.]/g,'').replace(/(\..*)\./g,'$1').replace(/^0+(?=\d)/,'').slice(0,6);setCv(parseFloat(v)>MAX_CUSTOM_FEE_RATE?String(MAX_CUSTOM_FEE_RATE):v);}}
                    onClick={e=>e.stopPropagation()}
                    style={{width:44,height:24,boxSizing:'border-box',background:C.card,border:'none',borderRadius:999,boxShadow:SH.line,color:C.ink,fontFamily:F.mono,fontSize:12.5,fontWeight:700,outline:'none',padding:'0 6px',textAlign:'center'}}
                    placeholder="–"/>
                : (cv ? <span style={{fontFamily:F.mono,fontWeight:700}}>{cv}</span> : null))
              : <span style={{fontFamily:F.mono,fontWeight:700}}>{shown}</span>}
          </div>
        );
      })}
    </div>
  );
}
export function effFee(feeKey, cv, vals) {
  if(feeKey==null) return null;
  if(feeKey==='custom'){ const v=parseFloat(cv); return v>0?v:null; }
  return vals?.[feeKey] || null;
}

// ── Batch stepper ────────────────────────────────────────────────────────────
export function BatchStepper({ batch, setBatch }) {
  const bs={width:40,height:40,borderRadius:'50%',border:'none',boxShadow:SH.line,background:C.card,color:C.ink,fontFamily:F.body,fontWeight:700,fontSize:20,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0};
  return (
    <div style={{marginTop:8}}>
      <div style={{display:'flex',alignItems:'center',gap:12}}>
        <button className="tap" style={bs} onClick={()=>setBatch(b=>Math.max(1,b-1))}>−</button>
        <div style={{flex:1,textAlign:'center'}}>
          <span key={batch} style={{...NUM(22),display:'inline-block',animation:anim('mob-numpop .35s cubic-bezier(.34,1.5,.5,1)')}}>×{batch}</span>
          <span style={{marginLeft:6,fontFamily:F.body,fontWeight:600,fontSize:12,color:C.dim}}>pulls</span>
        </div>
        <button className="tap" style={bs} onClick={()=>setBatch(b=>Math.min(99,b+1))}>+</button>
      </div>
      <div style={{display:'flex',gap:6,marginTop:10}}>
        {[1,5,10,25].map(q=><Pill key={q} on={batch===q} onClick={()=>setBatch(q)} style={{flex:1,fontFamily:F.mono}}>×{q}</Pill>)}
      </div>
    </div>
  );
}

// ── Buttons ──────────────────────────────────────────────────────────────────
// the one red action (green = the quiet ink variant, as on desktop .lever.green)
export function LeverBtn({ children, onClick, disabled, green, style }) {
  const bg = disabled ? (green ? '#c9c6c0' : CTA_OFF) : green ? C.ink : C.melon;
  return (
    <button className="mlever" onClick={disabled?undefined:onClick} disabled={disabled} style={{width:'100%',height:58,border:'none',cursor:disabled?'not-allowed':'pointer',borderRadius:20,
      background:bg,color:'#fff',fontFamily:F.body,fontWeight:800,fontSize:16,letterSpacing:.6,textTransform:'uppercase',
      display:'inline-flex',alignItems:'center',justifyContent:'center',gap:10,padding:'0 14px',...style}}>
      {children}
    </button>
  );
}
// secondary: white pill
export function GhostBtn({ children, onClick, disabled }) {
  return <button onClick={disabled?undefined:onClick} disabled={disabled} className={disabled?undefined:'mbtn'} style={{width:'100%',height:46,border:'none',cursor:disabled?'default':'pointer',borderRadius:999,background:C.card,boxShadow:SH.pill,color:C.ink,fontFamily:F.body,fontWeight:700,fontSize:13.5,opacity:disabled?.5:1}}>{children}</button>;
}

// ── Cost rows ────────────────────────────────────────────────────────────────
export function CostRow({ label, value, total, valueColor }) {
  return (
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,paddingTop:total?9:4,marginTop:total?4:0,borderTop:total?`1px solid ${C.rule}`:'none'}}>
      <Bod size={13} dim={!total} style={{fontWeight:total?700:500}}>{label}</Bod>
      <Mon size={13} color={valueColor||C.ink} style={{fontWeight:total?700:500,textAlign:'right'}}>{value}</Mon>
    </div>
  );
}

// ── Cabinet (legacy name) - now a plain cream block ──────────────────────────
export function Cabinet({ children, pad=16, style }) {
  return <Block pad={pad} style={style}>{children}</Block>;
}
